import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { logger } from 'logger'
import { Toggle, Button } from 'components/base'
import { selectors as OrganizationSelectors } from 'data/organizations'
import { selectors as UserPreferencesSelectors } from 'data/userPreferences'
import { selectors as UserSelectors } from 'data/users'
import { updateUserPreferences } from 'data/userPreferences/actions'
import { updateOrganization } from 'data/organizations/actions'

import NextData from './data'

const parseFlags = flagsString =>
  flagsString.split(',').filter(x => x.length > 1)

const stringifyFlags = flags => flags.join(',')

const toggleFlag = (flags, flagId) =>
  flags.includes(flagId)
    ? flags.filter(x => x !== flagId)
    : flags.concat(flagId)

const defaultFlag = 'next'

function SettingsNextSection({
  availableFlags,
  enabledFlags,
  title,
  description,
  onChange,
  canReset = false,
}) {
  return (
    <div>
      <div className="margin-top-huge margin-bottom-large">
        <div className="font-size-large shade-05 font-weight-semibold">
          {title}
        </div>
        <div className="shade-10">
          {description}
          <br />
          Your current flags are: <b>{stringifyFlags(enabledFlags)}</b>
          <br />
          {canReset && (
            <div>
              You can reset your flags by{' '}
              <button
                onClick={event => {
                  event.preventDefault()
                  onChange(defaultFlag)
                }}
              >
                clicking here
              </button>
            </div>
          )}
        </div>
      </div>
      {availableFlags.map(flag => (
        <div
          key={flag.id}
          className="width-full flexbox flexbox-align-center margin-bottom-large"
        >
          <div
            className="margin-right-normal flexbox-no-shrink"
            style={{ minWidth: 64 }}
          >
            <Toggle
              checked={enabledFlags.includes(flag.id)}
              onChange={async () => {
                await onChange(
                  stringifyFlags(toggleFlag(enabledFlags, flag.id))
                )
                flag.onSwitch && flag.onSwitch()
              }}
            />
          </div>
          <div className="flexbox-grow">
            <div className="font-weight-semibold">{flag.id}</div>
            <div>{flag.description}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

SettingsNextSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  availableFlags: PropTypes.array.isRequired,
  enabledFlags: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  canReset: PropTypes.bool,
}

function SettingsNext({
  organizationFlags,
  updateOrganization,
  userFlags,
  updateUserPreferences,
  userAbFlags,
  isOwner,
}) {
  return (
    <div className="margin-bottom-huge line-height-larger font-size-large">
      <SettingsNextSection
        title="Team Feature flags"
        description="These flags will affect all the team members. Use with caution."
        availableFlags={NextData.organization}
        enabledFlags={parseFlags(organizationFlags)}
        canReset
        onChange={featureFlags => updateOrganization({ featureFlags })}
      />
      <SettingsNextSection
        title="User Feature flags"
        description="These flags will only affect your user account."
        availableFlags={NextData.user}
        enabledFlags={parseFlags(userFlags)}
        canReset
        onChange={featureFlags => updateUserPreferences({ featureFlags })}
      />
      <SettingsNextSection
        title="User AB flags"
        description="Assigned AB Flags randomly to test features impact."
        availableFlags={[]}
        enabledFlags={userAbFlags}
        onChange={() => {
          logger.error(new Error('AbFlags cant be changed'))
        }}
      />

      {/* Onboarding */}
      <div className="margin-top-huge margin-bottom-large">
        <div className="font-size-large shade-05 font-weight-semibold">
          Onboarding
        </div>
        <div className="shade-10">
          Use this section to restore you're onboarding and test fresh users
          path.
        </div>
        <div className="margin-top-large">
          <Button.Secondary
            onClick={() => {
              updateUserPreferences({
                onboardingStep: isOwner ? 'invite' : 'channels',
                isChannelOnboardingDone: !isOwner,
              })
              setTimeout(() => {
                window.location = '/app/root'
              }, 300)
            }}
          >
            {isOwner ? 'Reset owner onboarding' : 'Reset user onboarding'}
          </Button.Secondary>
        </div>
      </div>
    </div>
  )
}

SettingsNext.propTypes = {
  organizationFlags: PropTypes.string.isRequired,
  updateOrganization: PropTypes.func.isRequired,
  userFlags: PropTypes.string.isRequired,
  updateUserPreferences: PropTypes.func.isRequired,
}

const enhance = connect(
  state => ({
    organizationFlags: OrganizationSelectors.getFeatureFlags(state),
    userFlags: UserPreferencesSelectors.getFeatureFlags(state),
    userAbFlags: UserSelectors.getAbFlags(state),
    isOwner: UserSelectors.isCurrentUserTheOwner(state),
  }),
  { updateUserPreferences, updateOrganization }
)

export default enhance(SettingsNext)
